/**
 * @copyright Copyright 2024-2024 Epic Systems Corporation
 * @file Helper functions for questionnaires
 * @module Epic.AppOrchard.Core.Questionnaire.Helpers
 */

import { Attestation_Workflow, ClientApplicationStatus, IAttestationModel } from "ao/data";
import {
	IQuestionnaireCategoryForEditing,
	IQuestionnaireCategoryForEditingWithDisplayValues,
	IQuestionnaireConditionAndSection,
	IQuestionnaireConditionPiece,
	IQuestionnaireQuestionForEditing,
	IQuestionnaireQuestionForEditingWithComputedDisplayValues,
	ISiteInfo,
	QuestionnaireQuestionType,
} from "ao/types";

const orDelimiter = "|";
const andDelimiter = ",";
const questionOptionDelimiter = ".";
const negateModifier = "!";

export function convertToCategoriesWithComputedDisplayValues(
	categories: IQuestionnaireCategoryForEditing[],
): [IQuestionnaireCategoryForEditingWithDisplayValues[], Map<number, number[]>] {
	const dependencyMap: Map<number, number[]> = new Map<number, number[]>();
	const newCategories = categories.map((categoryToCopy) => {
		return {
			...categoryToCopy,
			questions: categoryToCopy.questions.map((questionToCopy) => {
				return {
					...questionToCopy,
					shouldShow: true,
					conditionSections: parseCondition(questionToCopy, dependencyMap),
					options: questionToCopy.options
						.map((optionToCopy) => {
							return { ...optionToCopy };
						})
						.sort((option1, option2) => {
							return option1.orderNumber - option2.orderNumber;
						}),
				};
			}),
		};
	});

	return [newCategories, dependencyMap];
}

function parseCondition(
	question: IQuestionnaireQuestionForEditing,
	dependencyMap?: Map<number, number[]>,
): IQuestionnaireConditionAndSection[] {
	const orParts = question.displayCondition.split(orDelimiter);
	const orSections: IQuestionnaireConditionAndSection[] = [];
	if (!orParts || orParts.length === 0) {
		return orSections;
	}

	orParts.forEach((orPart) => {
		if (!orPart) {
			return;
		}
		const andParts = orPart.split(andDelimiter);

		const conditionPieces: IQuestionnaireConditionPiece[] = [];

		andParts.forEach((andPart) => {
			if (!andPart) {
				return;
			}
			const conditionParts = andPart.split(questionOptionDelimiter);
			const questionIdAndNegatePart = conditionParts[0];
			const negate = questionIdAndNegatePart.indexOf(negateModifier) > -1;
			const conditionQuestionId = parseInt(questionIdAndNegatePart.replace(negateModifier, ""));
			const conditionOptionId = parseInt(conditionParts[1]);

			if (isNaN(conditionQuestionId) || isNaN(conditionOptionId)) {
				return;
			}

			conditionPieces.push({ negate, questionId: conditionQuestionId, optionId: conditionOptionId });

			if (dependencyMap) {
				if (dependencyMap.has(conditionQuestionId)) {
					const dependentQuestions = dependencyMap.get(conditionQuestionId);
					dependentQuestions?.push(question.id);
				} else {
					dependencyMap.set(conditionQuestionId, [question.id]);
				}
			}
		});

		if (conditionPieces.length > 0) {
			orSections.push({ pieces: conditionPieces });
		}
	});

	return orSections;
}

export function copyCategories(
	categories: IQuestionnaireCategoryForEditingWithDisplayValues[],
): IQuestionnaireCategoryForEditingWithDisplayValues[] {
	const newCategories = categories.map((categoryToCopy) => {
		return {
			...categoryToCopy,
			questions: categoryToCopy.questions.map((questionToCopy) => {
				return {
					...questionToCopy,
					options: questionToCopy.options.map((optionToCopy) => {
						return { ...optionToCopy };
					}),
				};
			}),
		};
	});

	return newCategories;
}

export function setShouldShow(
	categories: IQuestionnaireCategoryForEditingWithDisplayValues[],
	dependencyMap: Map<number, number[]>,
	editedQuestionId?: number,
): void {
	const questions: IQuestionnaireQuestionForEditingWithComputedDisplayValues[] = [];
	categories.forEach((category) => questions.push(...category.questions));
	setShouldShowQuestions(questions, dependencyMap, editedQuestionId);
}

function setShouldShowQuestions(
	questions: IQuestionnaireQuestionForEditingWithComputedDisplayValues[],
	dependencyMap: Map<number, number[]>,
	editedQuestionId?: number,
): void {
	const questionMap = new Map<number, IQuestionnaireQuestionForEditingWithComputedDisplayValues>();

	questions.forEach((question) => {
		questionMap.set(question.id, question);
	});

	let questionsToProcess: number[] = editedQuestionId
		? [editedQuestionId]
		: [...questions.map((question) => question.id)];

	while (questionsToProcess.length > 0) {
		const questionIdToProcess = questionsToProcess.splice(0, 1)[0];
		const dependentQuestionIds = dependencyMap.get(questionIdToProcess);
		if (!dependentQuestionIds) {
			continue;
		}
		questionsToProcess.push(...processConditions(dependentQuestionIds, questionMap));
	}
}

function processConditions(
	questionsToProcess: number[],
	questions: Map<number, IQuestionnaireQuestionForEditingWithComputedDisplayValues>,
): number[] {
	const changedQuestions: number[] = [];

	questionsToProcess.forEach((questionId) => {
		const question = questions.get(questionId);
		if (!question) {
			return;
		}

		const anyConditionSectionIsTrue = question.conditionSections.find((andSection) => {
			const failedConditionPiece = andSection.pieces.find((conditionPiece) => {
				const questionForPiece = questions.get(conditionPiece.questionId);
				if (!questionForPiece) {
					return false; //malformed condition, ignore (treat condition as not-failed) and continue to next condition piece
				}
				if (!questionForPiece.shouldShow) {
					//question is not visible
					if (conditionPiece.negate) {
						return false; //question must not have a specific answer. Hidden questions are treated as having no answer, so condition met. Continue to next condition piece
					} else {
						return true; //question must have some answer. Hidden questions are treated as having no answer, so condition failed and can stop processing
					}
				}
				const optionForPiece = questionForPiece.options.find(
					(option) => option.id === conditionPiece.optionId,
				);
				if (!optionForPiece) {
					return false; //malformed condition, ignore (treat condition as not-failed) and continue to next condition piece
				}

				if (optionForPiece.selected) {
					//option is selected
					if (conditionPiece.negate) {
						return true; //option must not be selected. Condition failed and can stop processing
					} else {
						return false; //option must be selected. Condition met. Continue to next condition piece
					}
				} else {
					//option is not selected
					if (conditionPiece.negate) {
						return false; //option must not be selected. Condition met. Continue to next condition piece
					} else {
						return true; //option must be selected. Condition failed and can stop processing
					}
				}
			});
			return !failedConditionPiece;
		});

		if (anyConditionSectionIsTrue && !question.shouldShow) {
			question.shouldShow = true;
			changedQuestions.push(question.id);
		} else if (!anyConditionSectionIsTrue && question.shouldShow) {
			question.shouldShow = false;
			changedQuestions.push(question.id);
		}
	});

	return changedQuestions;
}

export function checkIfAllQuestionsAnswered(
	categories: IQuestionnaireCategoryForEditingWithDisplayValues[],
): boolean {
	for (const category of categories) {
		for (const question of category.questions) {
			if (question.shouldShow) {
				switch (question.type) {
					case QuestionnaireQuestionType.Freetext:
						if (!question.responseText || !question.responseText.trim()) {
							return false;
						}
						break;
					case QuestionnaireQuestionType.Radio:
						const selectedOption = question.options.find((option) => option.selected);
						if (
							!selectedOption ||
							(selectedOption.supportsFreetext &&
								(!selectedOption.freetextResponse || !selectedOption.freetextResponse.trim()))
						) {
							return false;
						}
						break;
					case QuestionnaireQuestionType.Checkboxes:
						const selectedOptions = question.options.filter((option) => option.selected);
						if (selectedOptions.length === 0) {
							return false;
						}
						const selectedOptionsMissingFreetext = selectedOptions.filter(
							(option) =>
								option.supportsFreetext &&
								(!option.freetextResponse || !option.freetextResponse.trim()),
						);
						if (selectedOptionsMissingFreetext.length > 0) {
							return false;
						}
						break;
				}
			}
		}
	}

	return true;
}

export function customerAppAttestationExists(siteInfo: ISiteInfo, attestation: IAttestationModel): boolean {
	const isFeatureEnabled = siteInfo.isCustomerAppAttestationEnabled ?? false;
	if (!isFeatureEnabled) {
		return false;
	}

	if (!attestation) {
		return false;
	}

	const categories = attestation?.categories ?? [];
	if (categories.length <= 0) {
		return false;
	}

	return true;
}

export function shouldSuppressAppAttestation(
	siteInfo: ISiteInfo,
	appStatus: ClientApplicationStatus,
	workflow: Attestation_Workflow,
): boolean {
	if (workflow === Attestation_Workflow.CreateApp) {
		return siteInfo.customerAppAttestationSuppressedDuringCreate ?? false;
	}

	const suppressedStatuses =
		workflow === Attestation_Workflow.NoCatalog
			? siteInfo.customerAppAttestationSuppressedAppStatusesWhenNoCatalog
			: workflow === Attestation_Workflow.MarkInternal
			? siteInfo.customerAppAttestationSuppressedAppStatusesWhenMarkingInternal
			: workflow === Attestation_Workflow.SixMonthLapse
			? siteInfo.customerAppAttestationSuppressedAppStatusesWhenSixMonthLapse
			: workflow === Attestation_Workflow.SubmitApp
			? siteInfo.customerAppAttestationSuppressedAppStatusesWhenSubmitting
			: workflow === Attestation_Workflow.NewVersion
			? siteInfo.customerAppAttestationSuppressedAppStatusesWhenCreatingNewVersion
			: workflow === Attestation_Workflow.ActivateForProd
			? siteInfo.customerAppAttestationSuppressedAppStatusesWhenActivatingProd
			: workflow === Attestation_Workflow.ActivateForNonProd
			? siteInfo.customerAppAttestationSuppressedAppStatusesWhenActivatingNonProd
			: workflow === Attestation_Workflow.EditApp
			? siteInfo.customerAppAttestationSuppressedAppStatusesWhenEditingApp
			: [];

	if (!suppressedStatuses || suppressedStatuses.length === 0) {
		return false;
	}

	return suppressedStatuses.indexOf(appStatus) >= 0;
}
